<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'
import { staticPageHref, accountHref } from '@/router/products'
import { useAuth } from '@/composables/useAuth'
import { useContracts } from '@/composables/useContracts'
import { useEcard } from '@/composables/useEcard'
import AccountLayout from '@/components/layout/AccountLayout.vue'
import logoUrl from '@/assets/logo.webp'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const { member, contractsLoading } = useAuth()
const { hasFsmeCard } = useContracts()

const currentLocale = computed<Locale>(() => (route.meta.locale as Locale) ?? DEFAULT_LOCALE)
const loginHref = computed(() => staticPageHref('login', currentLocale.value))
const dashboardHref = computed(() => accountHref('dashboard', currentLocale.value))
const contactHref = computed(() => accountHref('contact', currentLocale.value))
/** Same destination the dashboard's "De quoi as tu besoin ?" card points at, so the two CTAs
 *  wearing the same label land in the same place. */
const subscribeHref = computed(() => accountHref('subscribe', currentLocale.value))
useHead({ title: computed(() => `${t('auth.account.tpCard.navTitle')} — MySmerra`) })

/** Accès réservé. */
onMounted(() => {
  if (!member.value) router.replace(loginHref.value)
})

// La carte de tiers payant n'existe que pour un contrat actif remonté à FSME.
// Les contrats arrivent de façon asynchrone (`GET /contracts`) : on n'arbitre
// qu'une fois le chargement terminé, sinon un simple F5 éjecterait un adhérent
// légitime dont les contrats ne sont pas encore là.
watch(
  [member, contractsLoading, hasFsmeCard],
  ([currentMember, loading, hasCard]) => {
    if (!currentMember || loading) return
    if (!hasCard) router.replace(dashboardHref.value)
  },
  { immediate: true },
)

/** Tant que l'éligibilité n'est pas tranchée (contrats en vol), on n'affiche
 *  rien : évite un flash de la carte avant une éventuelle redirection. */
const canShowCard = computed(() => !!member.value && !contractsLoading.value && hasFsmeCard.value)

const birthDateLabel = computed(() => {
  const iso = member.value?.birthDate
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(locale.value, { day: '2-digit', month: '2-digit', year: 'numeric' })
})

/** Étapes « Comment utiliser ma carte » (clés i18n). */
const STEPS = ['pro', 'noAdvance', 'smerra'] as const

/** Téléchargement de la carte de tiers-payant (PDF). */
const { downloading, errorCode, errorMessage, download } = useEcard()
</script>

<template>
  <AccountLayout v-if="canShowCard && member" active="card">
    <section class="tpc">
      <!-- Fil d'ariane + titre -->
      <nav class="tpc__crumb" aria-label="breadcrumb">
        <span>{{ t('auth.account.tpCard.breadcrumbHome') }}</span>
        <span class="tpc__crumb-sep" aria-hidden="true">›</span>
        <span class="tpc__crumb-current">{{ t('auth.account.tpCard.navTitle') }}</span>
      </nav>
      <header class="tpc__head">
        <h1 class="tpc__title">{{ t('auth.account.tpCard.navTitle') }}</h1>
        <p class="tpc__subtitle">{{ t('auth.account.tpCard.subtitle') }}</p>
      </header>

      <div class="tpc__grid">
        <!-- Colonne principale -->
        <div class="tpc__main">
          <!-- Carte + téléchargement -->
          <div class="tpc__top">
            <div class="tp__card">
              <div class="tp__card-head">
                <img class="tp__card-logo" :src="logoUrl" alt="SMERRA" width="462" height="122" />
                <span class="tp__card-title">{{ t('auth.account.tpCard.title') }}</span>
              </div>
              <dl class="tp__card-info">
                <div class="tp__card-group">
                  <dt>{{ t('auth.account.info.memberNumber') }}</dt>
                  <dd class="tp__card-value tp__card-value--accent">{{ member.memberNumber }}</dd>
                </div>
                <div class="tp__card-group">
                  <dt>{{ t('auth.account.tpCard.beneficiary') }}</dt>
                  <dd class="tp__card-value">{{ member.firstName }} {{ member.lastName }}</dd>
                </div>
                <div class="tp__card-group">
                  <dt>{{ member.civility === 'mrs' ? t('auth.account.tpCard.bornFemale') : t('auth.account.tpCard.bornMale') }}</dt>
                  <dd class="tp__card-value">{{ birthDateLabel }}</dd>
                </div>
              </dl>
              <p class="tp__card-disclaimer">{{ t('auth.account.tpCard.disclaimer') }}</p>
            </div>

            <div class="dl">
              <h2 class="dl__title">{{ t('auth.account.tpCard.downloadTitle') }}</h2>
              <p class="dl__desc">{{ t('auth.account.tpCard.downloadDesc') }}</p>
              <button type="button" class="btn-dark" :disabled="downloading" @click="download('pdf')">{{ downloading ? t('auth.account.tpCard.downloading') : t('auth.account.tpCard.downloadCta') }}</button>
              <p v-if="errorCode" class="dl__update dl__update--error" role="alert">{{ errorMessage }}</p>
            </div>
          </div>

          <!-- Rappel : présenter sa carte -->
          <div class="present">
            <span class="present__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
                <path d="M12 11v5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                <circle cx="12" cy="7.5" r="1" fill="currentColor"/>
              </svg>
            </span>
            <div class="present__body">
              <p class="present__title">{{ t('auth.account.tpCard.presentTitle') }}</p>
              <p class="present__desc">{{ t('auth.account.tpCard.presentDesc') }}</p>
            </div>
          </div>

          <!-- Comment utiliser ma carte -->
          <section class="how">
            <h2 class="how__title">{{ t('auth.account.tpCard.howTitle') }}</h2>
            <ol class="how__grid">
              <li v-for="(step, i) in STEPS" :key="step" class="step">
                <span class="step__num" aria-hidden="true">{{ i + 1 }}</span>
                <span class="step__title">{{ t(`auth.account.tpCard.steps.${step}.title`) }}</span>
                <span class="step__desc">{{ t(`auth.account.tpCard.steps.${step}.desc`) }}</span>
              </li>
            </ol>
          </section>

          <!-- Besoin d'aide -->
          <article class="help">
            <span class="help__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
                <path d="M9.5 9.5a2.5 2.5 0 1 1 3.6 2.2c-.7.4-1.1.9-1.1 1.8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                <circle cx="12" cy="16.5" r="1" fill="currentColor"/>
              </svg>
            </span>
            <div class="help__body">
              <h3 class="help__title">{{ t('auth.account.tpCard.help.title') }}</h3>
              <p class="help__desc">{{ t('auth.account.tpCard.help.desc') }}</p>
              <RouterLink class="help__cta" :to="contactHref">{{ t('auth.account.tpCard.help.cta') }}</RouterLink>
            </div>
          </article>
        </div>

        <!-- Colonne latérale : infos + promo produits -->
        <aside class="tpc__aside">
          <ul class="facts">
            <li class="fact">
              <span class="fact__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <circle cx="12" cy="8" r="3.5" fill="none" stroke="currentColor" stroke-width="1.6"/>
                  <path d="M5 20c0-3.3 3.1-6 7-6s7 2.7 7 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                </svg>
              </span>
              <div class="fact__body">
                <span class="fact__title">{{ t('auth.account.tpCard.personalTitle') }}</span>
                <span class="fact__desc">{{ t('auth.account.tpCard.personalDesc') }}</span>
              </div>
            </li>
            <li class="fact">
              <span class="fact__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path d="M20 12a8 8 0 1 1-2.3-5.6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                  <path d="M20 4v4h-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <div class="fact__body">
                <span class="fact__title">{{ t('auth.account.tpCard.updateTitle') }}</span>
                <span class="fact__desc">{{ t('auth.account.tpCard.updateDesc') }}</span>
              </div>
            </li>
          </ul>

          <article class="promo">
            <h2 class="promo__title">{{ t('auth.account.tpCard.promo.title') }}</h2>
            <p class="promo__desc">{{ t('auth.account.tpCard.promo.desc') }}</p>
            <!-- A RouterLink and no longer a plain <a href>: the destination is now a route of
                 the connected space, which a native link would reload in full instead of
                 navigating to. -->
            <RouterLink class="btn-dark promo__cta" :to="subscribeHref">
              {{ t('auth.account.tpCard.promo.cta') }}
            </RouterLink>
          </article>
        </aside>
      </div>
    </section>
  </AccountLayout>
</template>

<style scoped>
.tpc { display: flex; flex-direction: column; gap: 1rem; }

/* Fil d'ariane + titre */
.tpc__crumb { display: flex; align-items: center; gap: 0.4rem; font-size: var(--text-sm); color: var(--text-muted); }
.tpc__crumb-current { color: var(--color-chocolate); font-weight: 600; }
.tpc__title { font-size: var(--text-xl); font-weight: 700; color: var(--color-chocolate); }
.tpc__subtitle { margin-top: 0.15rem; font-size: var(--text-sm); color: var(--text-muted); }

/* Grille principale / latérale */
.tpc__grid {
  display: grid;
  grid-template-columns: 1.7fr 1fr;
  gap: 1.25rem;
  align-items: start;
  margin-top: 0.25rem;
}
.tpc__main { display: flex; flex-direction: column; gap: 1.25rem; }
.tpc__aside { display: flex; flex-direction: column; gap: 1.25rem; }

/* Carte + bloc de téléchargement côte à côte */
.tpc__top { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; align-items: start; }

/* Carte de tiers payant (faux visuel) */
.tp__card {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  padding: 0.9rem 1rem;
  border-radius: var(--radius-lg);
  color: var(--color-chocolate);
  background: url('/account/carte-tiers-payant.webp') right center / cover no-repeat,
    color-mix(in oklab, var(--color-peach) 60%, white);
  box-shadow: var(--shadow-sm);
}
.tp__card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 0.5rem; }
.tp__card-logo { height: 1.5rem; width: auto; }
.tp__card-title {
  font-weight: 800;
  font-size: var(--text-xs);
  line-height: 1.15;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--color-cherry);
  text-align: right;
}
.tp__card-info { display: flex; flex-direction: column; gap: 0.5rem; margin: 0; }
.tp__card-group { display: flex; flex-direction: column; }
.tp__card-group dt { font-size: 0.7rem; color: var(--color-chocolate); }
.tp__card-value { font-size: var(--text-sm); font-weight: 700; line-height: 1.2; }
.tp__card-value--accent { color: var(--color-cherry); }
.tp__card-disclaimer { font-size: 0.6rem; color: var(--color-chocolate); line-height: 1.3; }

/* Bloc téléchargement */
.dl {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
  height: 100%;
}
.dl__title { font-size: var(--text-lg); font-weight: 700; color: var(--color-chocolate); }
.dl__desc { font-size: var(--text-sm); color: var(--text-muted); }
.dl__update { margin-top: 0.1rem; font-size: var(--text-xs); color: var(--text-muted); }
.dl__update--error { color: var(--color-cherry); }

/* Bandeau « présentez votre carte » */
.present {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.9rem 1rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--bg-soft);
}
.present__icon { color: var(--primary); flex-shrink: 0; margin-top: 0.1rem; }
.present__title { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.present__desc { margin-top: 0.1rem; font-size: var(--text-xs); color: var(--text-muted); line-height: var(--leading-normal); }

/* Comment utiliser */
.how {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
}
.how__title { font-size: var(--text-lg); font-weight: 700; color: var(--color-chocolate); }
.how__grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1rem; margin: 0; padding: 0; list-style: none; }
.step {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 0.55rem;
  row-gap: 0.3rem;
  align-content: start;
}
.step__num {
  grid-row: 1 / span 2;
  display: grid;
  place-items: center;
  width: 1.65rem;
  height: 1.65rem;
  border-radius: var(--radius-pill);
  background: color-mix(in oklab, var(--primary) 14%, white);
  color: var(--primary);
  font-size: var(--text-xs);
  font-weight: 700;
}
.step__title { grid-column: 2; font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.step__desc { grid-column: 2; font-size: var(--text-xs); color: var(--text-muted); line-height: var(--leading-normal); }

/* Besoin d'aide */
.help {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  padding: 1rem;
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-lg);
  background: var(--bg-soft);
}
.help__icon {
  display: grid;
  place-items: center;
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  border-radius: var(--radius-lg);
  color: var(--text-muted);
  background: var(--color-white);
}
.help__title { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.help__desc { margin-top: 0.15rem; font-size: var(--text-xs); color: var(--text-muted); line-height: var(--leading-normal); }
.help__cta {
  display: inline-block;
  margin-top: 0.3rem;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
}
.help__cta:hover { color: var(--color-cherry); }

/* Faits (validité / usage / mise à jour) */
.facts {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
  padding: 0.5rem 0.75rem;
  list-style: none;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
}
.fact { display: flex; align-items: flex-start; gap: 0.7rem; padding: 0.6rem 0.25rem; }
.fact + .fact { border-top: 1px solid var(--border); }
.fact__icon { color: var(--primary); flex-shrink: 0; margin-top: 0.1rem; }
.fact__body { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.fact__title { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.fact__desc { font-size: var(--text-xs); color: var(--text-muted); line-height: var(--leading-normal); }

/* Promo produits */
.promo {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  align-items: flex-start;
  padding: 1.25rem;
  border-radius: var(--radius-xl);
  background: color-mix(in oklab, var(--color-peach) 45%, white);
  border: 1px solid color-mix(in oklab, var(--primary) 25%, white);
  box-shadow: var(--shadow-sm);
}
.promo__title { font-size: var(--text-base); font-weight: 700; color: var(--color-chocolate); line-height: 1.25; }
.promo__desc { font-size: var(--text-xs); color: var(--color-chocolate); line-height: var(--leading-normal); }
.promo__cta { margin-top: 0.2rem; text-decoration: none; }

/* Bouton sombre */
.btn-dark {
  padding: 0.75rem 1.6rem;
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--color-chocolate);
  color: var(--color-white);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--dur-fast) var(--ease-out);
}
.btn-dark:hover { background: color-mix(in oklab, var(--color-chocolate) 85%, white); }

@media (max-width: 64rem) {
  .tpc__grid { grid-template-columns: 1fr; }
}
@media (max-width: 48rem) {
  .tpc__top { grid-template-columns: 1fr; }
  .how__grid { grid-template-columns: 1fr; }
}
</style>
